import { RegionCode } from '@kaa/api/customers';
import { i18nKeys } from '@kaa/i18n/customers/keys';
import {
  SwActionGroup,
  SwButton,
  SwColumn,
  SwSlider,
  toggleModalById,
} from '@kaa/ui-flanders/components';
import get from 'lodash.get';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthState } from '@kaa/auth/common';
import { getConfig } from '../../../common/config';
import {
  EventPathname,
  ModalType,
  sendCloseModalEvent,
  sendModalInteractionEvent,
  sendVirtualPageViewEvent,
} from '../../utils/google-analytics';
import './OnboardingModal.style.scss';
import { Slide } from './OnBoardingModal.type';
import { getServiceVoucherSlideLokaliseKeyPerRegion } from './OnboardingModal.utils';
import { OnboardingModalSlide } from './OnboardingModalSlide';
import { AuthContext } from '../../types';

type OnboardingModalProps = {
  modalId: string;
  slides: Slide[];
};

export const OnboardingModal = ({ slides, modalId }: OnboardingModalProps) => {
  const { t } = useTranslation();
  const { selectedCustomerId } = useAuthState<AuthContext>();

  const regionCode = get(getConfig(), 'buildConfig.regionCode') as RegionCode;

  useEffect(() => {
    sendVirtualPageViewEvent(`${EventPathname.ONBOARDING_MODAL}/step-1`, {
      customerId: selectedCustomerId,
      flowName: 'Onboarding Modal',
    });
  }, []);

  return (
    <>
      <SwSlider
        className="onboarding"
        dots
        equalHeight
        keyboardNavigation
        arrows={false}
        bottomChildren={slides.map(
          (slide) => ({ next, previous, selectedIndex }: any) => (
            <SwColumn
              className="vl-slider__frame onboarding-description"
              key={selectedIndex}
            >
              <p style={{ minHeight: '10rem' }}>
                {t(
                  getServiceVoucherSlideLokaliseKeyPerRegion(slide, regionCode),
                )}
              </p>
              {slides.length > 1 && (
                <SwActionGroup>
                  <SwButton
                    onClick={(event) => {
                      previous(event);

                      sendModalInteractionEvent(
                        `Goto step ${selectedIndex}`,
                        `OnboardingModal ${
                          slide.modalName
                        } step${selectedIndex + 1}`,
                        ModalType.TUTORIAL,
                        `${
                          EventPathname.ONBOARDING_MODAL
                        }/step-${selectedIndex - 1}`,
                        {
                          linkTextLocale: t(i18nKeys.general.previous),
                          linkText: 'Onboarding Modal Previous Button',
                        },
                      );
                      sendVirtualPageViewEvent(
                        `${
                          EventPathname.ONBOARDING_MODAL
                        }/step-${selectedIndex - 1}`,
                        {
                          customerId: selectedCustomerId,
                          flowName: 'Onboarding Modal',
                        },
                      );
                    }}
                    modDisabled={!selectedIndex}
                  >
                    {t(i18nKeys.general.previous)}
                  </SwButton>
                  {selectedIndex === slides.length - 1 ? (
                    <SwButton
                      onClick={() => {
                        toggleModalById(modalId);

                        sendCloseModalEvent(
                          `OnboardingModal ${slide.modalName}`,
                          ModalType.TUTORIAL,
                          `${EventPathname.ONBOARDING_MODAL}/step-${selectedIndex}`,
                          {
                            linkTextLocale: t(i18nKeys.general.close),
                            linkText: 'Onboarding Modal Close Button',
                          },
                        );
                      }}
                    >
                      {t(i18nKeys.general.close)}
                    </SwButton>
                  ) : (
                    <SwButton
                      onClick={(event) => {
                        next(event);

                        sendModalInteractionEvent(
                          `Goto step ${selectedIndex + 2}`,
                          `OnboardingModal ${
                            slide.modalName
                          } step${selectedIndex + 2}`,
                          ModalType.TUTORIAL,
                          `${
                            EventPathname.ONBOARDING_MODAL
                          }/step-${selectedIndex + 1}`,
                          {
                            linkTextLocale: t(i18nKeys.general.next),
                            linkText: 'Onboarding Modal Next Button',
                          },
                        );

                        sendVirtualPageViewEvent(
                          `${
                            EventPathname.ONBOARDING_MODAL
                          }/step-${selectedIndex + 1}`,
                          {
                            customerId: selectedCustomerId,
                            flowName: 'Onboarding Modal',
                          },
                        );
                      }}
                      modDisabled={selectedIndex === slides.length - 1}
                    >
                      {t(i18nKeys.general.next)}
                    </SwButton>
                  )}
                </SwActionGroup>
              )}
            </SwColumn>
          ),
        )}
      >
        {slides.map((slide, index) => (
          <OnboardingModalSlide
            slide={slide}
            isFirstSlide={index === 0}
            key={slide.content.title}
          />
        ))}
      </SwSlider>
    </>
  );
};
